import React, { useEffect, useState } from "react";
import discussion from "assets/discussion.svg";
import fleche from "assets/fleche.svg";
import { getMyProblems } from "../../../server/Exercises CRUD/Requests";
import { Link } from "react-router-dom";
import { getSchoolClassLabel } from "components/common/utils";

interface SubmissionProps {
  subject: string;
  reference: string;
  timeElapsed: string;
}

const formatTimeElapsed = (date: string) => {
  try {
    const now = new Date();
    const submissionDate = new Date(date);

    if (isNaN(submissionDate.getTime())) {
      return "Néant";
    }

    const diffInMinutes = Math.floor(
      (now.getTime() - submissionDate.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 60) {
      return `Il y a ${diffInMinutes} min`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `Il y a ${diffInHours} h`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `Il y a ${diffInDays} j`;
  } catch {
    return "Néant";
  }
};

const SubmissionItem: React.FC<SubmissionProps> = ({
  subject,
  reference,
  timeElapsed,
}) => (
  <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 hover:bg-gray-50 rounded-lg transition-colors">
    <img
      src={discussion}
      alt="Soumission"
      className="w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0"
    />
    <div className="flex flex-col flex-1 min-w-0">
      <div className="text-black text-sm font-semibold truncate">
        {reference}
      </div>
      <div className="text-zinc-500 text-xs font-normal truncate">
        {subject}
      </div>
      <div className="text-zinc-500 text-xs font-normal">{timeElapsed}</div>
    </div>
  </div>
);

const Separator: React.FC = () => (
  <div className="w-full border-b border-gray-200 mx-4" />
);

const Discussion: React.FC = () => {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatReference = (submission: any): string => {
    try {
      const type = submission?.referenceType;
      if (type === "livre" && submission?.referenceBook) {
        const b = submission.referenceBook;
        const name = b?.name || "Livre";
        const page = b?.page != null ? ` p.${b.page}` : "";
        const ex = b?.exerciseNumber != null ? ` ex.${b.exerciseNumber}` : "";
        return `${name}${page}${ex}`.trim();
      }
      if (type === "epreuve" && submission?.referenceTest) {
        const t = submission.referenceTest;
        const parts = [
          t?.schoolName,
          t?.year,
          t?.session,
          getSchoolClassLabel(t?.class),
        ]
          .filter((x) => x != null && x !== "")
          .join(" · ");
        return parts || "Épreuve";
      }
      if (type === "other" && submission?.referenceOther) {
        return submission.referenceOther?.info || "Autre";
      }
      return "Sans référence";
    } catch {
      return "Sans référence";
    }
  };

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        const response = await getMyProblems();

        if (response.status === 200 && Array.isArray(response.body)) {
          // Trier les soumissions par date (du plus récent au plus ancien)
          const sortedSubmissions = response.body.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );

          // Prendre les 5 premières soumissions (au lieu de 3)
          setSubmissions(sortedSubmissions.slice(0, 5));
        } else {
          throw new Error("Erreur lors de la récupération des soumissions");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération des soumissions:", err);
        setError(
          "Une erreur est survenue lors de la récupération des soumissions",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSubmissions();
  }, []);

  if (loading) {
    return (
      <div className="space-y-2">
        {/* <div className="text-black text-base sm:text-lg font-bold font-['Open Sans'] leading-none mb-3 sm:mb-4">
          Dernières soumissions
        </div> */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gray-200 rounded-full flex-shrink-0" />
              <div className="flex flex-col space-y-2 flex-1">
                <div className="h-4 bg-gray-200 rounded w-32 sm:w-40" />
                <div className="h-3 bg-gray-200 rounded w-48 sm:w-56" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
            </div>
            {i < 2 && <Separator />}
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <div className="text-black text-base sm:text-lg font-bold font-['Open Sans'] leading-none mb-3 sm:mb-4">
          Dernières soumissions
        </div>
        <div className="text-red-500 text-sm p-3">{error}</div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="space-y-2">
        {/* <div className="text-black text-base sm:text-lg font-bold font-['Open Sans'] leading-none mb-3 sm:mb-4">
          Dernières soumissions
        </div> */}
        <div className="text-zinc-500 text-sm p-3">
          Aucune soumission récente
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* <div className="text-black text-base sm:text-lg font-bold font-['Open Sans'] leading-none mb-3 sm:mb-4">
        Dernières soumissions
      </div> */}
      <div className="space-y-1">
        {submissions.map((submission, index) => (
          <React.Fragment key={submission._id}>
            <SubmissionItem
              subject={submission.matiere || "Sans matière"}
              reference={formatReference(submission)}
              timeElapsed={formatTimeElapsed(submission.createdAt)}
            />
            {index < submissions.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      </div>
      <Link
        to="/submissions"
        className="text-blue-800 text-xs font-normal font-['Inter'] underline leading-3 mt-3 sm:mt-4 block hover:text-blue-900 transition-colors"
      >
        Voir Plus {">"}
      </Link>
    </div>
  );
};

export default Discussion;
